import Hero from "@/components/landing/Hero";
import { lazy, Suspense } from "react";
import SEO from "@/components/SEO";
import PerformanceOptimizer from "@/components/PerformanceOptimizer";

// Lazy load non-critical components to reduce initial bundle size
const Benefits = lazy(() => import("@/components/landing/Benefits"));
const HowItWorks = lazy(() => import("@/components/landing/HowItWorks"));
const Team = lazy(() => import("@/components/landing/Team"));
const BrandCTASection = lazy(() => import("@/components/landing/BrandCTASection"));
const CTASection = lazy(() => import("@/components/landing/CTASection"));
const BrandMarquee = lazy(() => import("@/components/landing/BrandMarquee"));
const OurProduct = lazy(() => import("@/components/landing/OurProduct"));
const Footer = lazy(() => import("@/components/landing/Footer"));
const Partners = lazy(() => import("@/components/landing/Partners"));


const Index = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://cloutfit.ai/#organization',
        name: 'Cloutfit',
        alternateName: 'OutfitAlliance',
        url: 'https://cloutfit.ai',
        sameAs: [
          'https://www.instagram.com/trycloutfit/',
          'https://www.linkedin.com/company/cloutfit-ai',
          'https://www.tiktok.com/@outfitalliance'
        ],
        description: 'AI that matches influencer posts to in-stock products so brands turn social into sales.',
        logo: {
          '@type': 'ImageObject',
          url: 'https://cloutfit.ai/lovable-uploads/42367874-987e-4a41-a126-f72fb38bc740.png'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          availableLanguage: 'English',
          url: 'https://cloutfit.ai'
        }
      },
      {
        '@type': 'WebSite',
        '@id': 'https://cloutfit.ai/#website',
        url: 'https://cloutfit.ai',
        name: 'Cloutfit.ai',
        description: 'AI that matches influencer posts to in-stock products so brands turn social into sales.',
        publisher: {
          '@id': 'https://cloutfit.ai/#organization'
        },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: 'https://cloutfit.ai/search?q={search_term_string}'
          },
          'query-input': 'required name=search_term_string'
        },
        inLanguage: 'en-US'
      },
      {
        '@type': 'WebPage',
        '@id': 'https://cloutfit.ai/#webpage',
        url: 'https://cloutfit.ai',
        name: 'Cloutfit.ai | AI-powered influencer commerce for fashion',
        description: 'AI that matches influencer posts to in-stock products so brands turn social into sales.',
        isPartOf: {
          '@id': 'https://cloutfit.ai/#website'
        },
        about: {
          '@id': 'https://cloutfit.ai/#organization'
        },
        inLanguage: 'en-US'
      }
    ]
  };

  return (
    <>
      <SEO
        title="Cloutfit.ai | AI-powered influencer commerce for fashion"
        description="AI that matches influencer posts to in-stock products so brands turn social into sales."
        keywords="influencer marketing, fashion brands, AI commerce, outfit matching, social media monetization, fashion influencers, brand partnerships, cloutfit"
        structuredData={structuredData}
      />
      <PerformanceOptimizer />
      <main>
        <Hero />
        <Suspense fallback={
          <div className="py-8">
            <div className="container px-4">
              <div className="flex justify-center gap-8 items-center">
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="h-12 w-16 skeleton-text rounded-lg" />
                ))}
              </div>
            </div>
          </div>
        }>
          <BrandMarquee />
        </Suspense>
        <Suspense fallback={
          <div className="py-16">
            <div className="container px-4">
              <div className="space-y-8">
                <div className="h-12 w-2/3 mx-auto skeleton-text rounded-lg" />
                <div className="h-6 w-1/2 mx-auto skeleton-text rounded-lg" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                  <div className="h-64 skeleton-text rounded-2xl" />
                  <div className="space-y-4">
                    <div className="h-6 skeleton-text rounded-lg" />
                    <div className="h-6 w-4/5 skeleton-text rounded-lg" />
                    <div className="h-6 w-3/5 skeleton-text rounded-lg" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        }>
          <OurProduct />
        </Suspense>
        <Suspense fallback={
          <div className="py-16">
            <div className="container px-4">
              <div className="space-y-12">
                <div className="text-center space-y-4">
                  <div className="h-10 w-1/2 mx-auto skeleton-text rounded-lg" />
                  <div className="h-6 w-2/3 mx-auto skeleton-text rounded-lg" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {Array.from({ length: 3 }, (_, i) => (
                    <div key={i} className="space-y-4">
                      <div className="h-16 w-16 mx-auto skeleton-text rounded-full" />
                      <div className="h-6 skeleton-text rounded-lg" />
                      <div className="h-4 skeleton-text rounded-lg" />
                      <div className="h-4 w-4/5 skeleton-text rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        }>
          <Benefits />
        </Suspense>
        <Suspense fallback={
          <div className="py-16">
            <div className="container px-4">
              <div className="space-y-12">
                <div className="text-center">
                  <div className="h-10 w-1/3 mx-auto skeleton-text rounded-lg" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {Array.from({ length: 4 }, (_, i) => (
                    <div key={i} className="text-center space-y-4">
                      <div className="h-24 w-24 mx-auto skeleton-text rounded-full" />
                      <div className="h-6 skeleton-text rounded-lg" />
                      <div className="h-4 skeleton-text rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        }>
          <HowItWorks />
        </Suspense>
        <Suspense fallback={
          <div className="py-16">
            <div className="container px-4">
              <div className="space-y-12">
                <div className="text-center">
                  <div className="h-10 w-1/4 mx-auto skeleton-text rounded-lg" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {Array.from({ length: 3 }, (_, i) => (
                    <div key={i} className="text-center space-y-4">
                      <div className="h-16 w-16 mx-auto skeleton-text rounded-full" />
                      <div className="h-5 skeleton-text rounded-lg" />
                      <div className="h-4 skeleton-text rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        }>
          <Team />
        </Suspense>
        <Suspense fallback={
          <div className="py-12">
            <div className="container px-4 text-center space-y-6">
              <div className="h-8 w-1/2 mx-auto skeleton-text rounded-lg" />
              <div className="h-12 w-32 mx-auto skeleton-text rounded-lg" />
            </div>
          </div>
        }>
          <BrandCTASection />
        </Suspense>
        <Suspense fallback={
          <div className="py-12">
            <div className="container px-4 text-center space-y-6">
              <div className="h-8 w-1/3 mx-auto skeleton-text rounded-lg" />
              <div className="h-12 w-32 mx-auto skeleton-text rounded-lg" />
            </div>
          </div>
        }>
          <CTASection />
        </Suspense>
        <Suspense fallback={
          <div className="py-8">
            <div className="container px-4">
              <div className="flex justify-center gap-6">
                {Array.from({ length: 4 }, (_, i) => (
                  <div key={i} className="h-8 w-20 skeleton-text rounded-lg" />
                ))}
              </div>
            </div>
          </div>
        }>
          <Partners />
        </Suspense>
        <Suspense fallback={
          <div className="py-16 bg-muted">
            <div className="container px-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                <div className="space-y-4">
                  <div className="h-6 w-1/2 skeleton-text rounded-lg" />
                  <div className="h-4 skeleton-text rounded-lg" />
                  <div className="h-4 w-4/5 skeleton-text rounded-lg" />
                </div>
                {Array.from({ length: 3 }, (_, i) => (
                  <div key={i} className="space-y-4">
                    <div className="h-5 skeleton-text rounded-lg" />
                    <div className="space-y-2">
                      {Array.from({ length: 4 }, (_, j) => (
                        <div key={j} className="h-4 w-3/4 skeleton-text rounded-lg" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        }>
          <Footer />
        </Suspense>
      </main>
    </>
  );
};

export default Index;
